/**
 * Layout components - Sidebar, Navigation, Headers, etc.
 * 
 * REFACTORED: Navigation configuration centralized to avoid duplication
 */
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Home, User, LogOut, ChevronLeft, Award, Trophy, 
  LayoutDashboard, Shield, CalendarDays, Sparkles, Target, BookOpen
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTenant } from "../../contexts/TenantContext";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { NotificationBell } from "./NotificationBell";
import { LOGO_URL } from "../../utils/constants";

// ============================================
// NAVIGATION CONFIGURATION (Single Source of Truth)
// ============================================

/**
 * Navigation items configuration
 * @param {Object} options - User role flags
 * @returns {Array} Filtered navigation items for the current user
 */
const getNavItems = ({ isManager, isAdmin, isTeamLeader, isAgent, isSuperAdmin }) => {
  const isManagerOrAdmin = isManager || isAdmin || isSuperAdmin;
  const canViewDashboard = isManagerOrAdmin || isAgent || isTeamLeader;
  
  const allItems = [
    { path: "/", icon: Home, label: "Dnevni zadaci", mobileLabel: "Početna", showFor: "all" },
    { path: "/calendar", icon: CalendarDays, label: "Kalendar", showFor: "all" },
    { path: "/badges", icon: Award, label: "Bedževi", showFor: "agent_teamleader" },
    { path: "/team-leader-dashboard", icon: LayoutDashboard, label: "Moj Tim", showFor: "team_leader" },
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", showFor: "dashboard_viewers" },
    { path: "/manager-panel", icon: Shield, label: "Manager Panel", mobileLabel: "Manager", showFor: "manager" },
    { path: "/admin", icon: Shield, label: "Admin Panel", mobileLabel: "Admin", showFor: "admin" },
    { path: "/discovery", icon: Sparkles, label: "AI Pretraga", mobileLabel: "AI", showFor: "all" },
    { path: "/target-lists", icon: Target, label: "Ciljne liste", mobileLabel: "Liste", showFor: "manager_admin" },
    { path: "/playbook", icon: BookOpen, label: "Playbook", showFor: "all" },
    { path: "/super-admin", icon: Shield, label: "Super Admin", mobileLabel: "SA", showFor: "super_admin" },
    { path: "/profile", icon: User, label: "Profil", showFor: "all" },
  ];
  
  return allItems.filter(item => {
    switch (item.showFor) {
      case "all": return true;
      case "manager": return isManager;
      case "admin": return isAdmin || isSuperAdmin;
      case "team_leader": return isTeamLeader;
      case "manager_admin": return isManagerOrAdmin;
      case "dashboard_viewers": return canViewDashboard;
      case "agent_teamleader": return isAgent || isTeamLeader;
      case "super_admin": return isSuperAdmin;
      default: return false;
    }
  });
};

/**
 * Check if a nav item is active based on current path
 */
const isNavItemActive = (itemPath, currentPath) => {
  if (itemPath === currentPath) return true;
  if (itemPath === "/dashboard" && currentPath.startsWith("/dashboard")) return true;
  if (itemPath === "/admin" && currentPath.startsWith("/admin")) return true;
  if (itemPath === "/super-admin" && currentPath.startsWith("/super-admin")) return true;
  if (itemPath === "/manager-panel" && currentPath.startsWith("/manager-panel")) return true;
  if (itemPath === "/target-lists" && currentPath.startsWith("/target-lists")) return true;
  if (itemPath === "/playbook" && currentPath.startsWith("/playbook")) return true;
  return false;
};

/**
 * Get role label for display
 */
const getRoleLabel = (role) => {
  const roles = { 
    agent: 'Agent', 
    manager: 'Manager', 
    admin: 'Administrator', 
    team_leader: 'Team Leader' 
  };
  return roles[role] || role;
};

// ============================================
// LAYOUT COMPONENTS
// ============================================

// Desktop Sidebar
export const DesktopSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useTenant();
  
  const logoSrc = branding.logo_url || LOGO_URL;
  const navItems = getNavItems({
    isManager: user?.role === 'manager',
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isTeamLeader: user?.role === 'team_leader',
    isAgent: user?.role === 'agent',
    isSuperAdmin: user?.role === 'super_admin'
  });

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
    toast.success("Odjavljeni ste");
  };

  return (
    <aside className="desktop-sidebar show-only-desktop" data-testid="desktop-sidebar">
      <div className="p-6 border-b border-gray-100">
        <img 
          src={logoSrc} 
          alt={branding.company_name || "Logo"} 
          className="h-10 w-auto"
          data-testid="desktop-logo"
        />
      </div>
      
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1B3F8F]/10 flex items-center justify-center">
            <User size={20} className="text-[#1B3F8F]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">{user?.name}</p>
            <p className="text-xs text-gray-500">{getRoleLabel(user?.role)}</p>
          </div>
          <NotificationBell />
        </div>
      </div>
      
      <nav className="flex-1 py-4">
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive = isNavItemActive(path, location.pathname);
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              data-testid={`desktop-nav-${label.toLowerCase().replace(' ', '-')}`}
              className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${
                isActive 
                  ? "bg-[#1B3F8F]/10 text-[#1B3F8F] border-r-3 border-[#1B3F8F]" 
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              <span className="font-medium text-sm">{label}</span>
            </button>
          );
        })}
      </nav>
      
      <div className="p-4 border-t border-gray-100">
        <button 
          onClick={handleLogout}
          data-testid="desktop-logout-button"
          className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
        >
          <LogOut size={20} />
          <span className="font-medium text-sm">Odjava</span>
        </button>
      </div>
    </aside>
  );
};

// Bottom Navigation (Mobile only)
export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const navItems = getNavItems({
    isManager: user?.role === 'manager',
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isTeamLeader: user?.role === 'team_leader',
    isAgent: user?.role === 'agent',
    isSuperAdmin: user?.role === 'super_admin'
  });

  return (
    <nav 
      className="fixed bottom-8 left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-[416px] bg-white border border-gray-200 h-14 flex items-center justify-around z-[9999] rounded-2xl shadow-lg lg:hidden"
      data-testid="bottom-nav"
    >
      {navItems.map(({ path, icon: Icon, label, mobileLabel }) => {
        const isActive = isNavItemActive(path, location.pathname);
        const displayLabel = mobileLabel || label;
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            data-testid={`nav-${displayLabel.toLowerCase()}`}
            className={`flex flex-col items-center gap-1 py-2 px-3 ${
              isActive 
                ? "text-[#1B3F8F]" 
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
            <span className="text-[10px] font-medium">{displayLabel}</span>
          </button>
        );
      })}
    </nav>
  );
};

// App Header (Mobile)
export const AppHeader = ({ title, showLogout = false, showBack = false, onBack }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
    toast.success("Odjavljeni ste");
  };

  return (
    <header 
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 h-14 flex items-center px-4 justify-between lg:hidden"
      data-testid="app-header"
    >
      <div className="flex items-center gap-2">
        {showBack && (
          <button 
            onClick={onBack}
            data-testid="back-button"
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 p-1.5 rounded-md transition-colors -ml-1"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        <h1 className="font-semibold text-gray-900 text-lg tracking-tight truncate max-w-[200px]">{title}</h1>
      </div>
      <div className="flex items-center gap-1">
        <NotificationBell />
        {showLogout && (
          <button 
            onClick={handleLogout}
            data-testid="logout-button"
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-md transition-colors"
          >
            <LogOut size={20} />
          </button>
        )}
      </div>
    </header>
  );
};

// Desktop Header
export const DesktopHeader = ({ title, showBack = false, onBack }) => {
  return (
    <header className="desktop-header show-only-desktop" data-testid="desktop-header">
      <div className="flex items-center gap-3">
        {showBack && (
          <button 
            onClick={onBack}
            data-testid="desktop-back-button"
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        <h1 className="font-semibold text-gray-900 text-xl tracking-tight">{title}</h1>
      </div>
    </header>
  );
};

// Responsive Layout Wrapper
export const ResponsiveLayout = ({ children, title, showBack = false, onBack, showLogout = false }) => {
  const isDesktop = useIsDesktop();
  
  if (isDesktop) {
    return (
      <div className="desktop-layout">
        <DesktopSidebar />
        <main className="desktop-main">
          <DesktopHeader title={title} showBack={showBack} onBack={onBack} />
          <div className="desktop-container">
            <div className="desktop-page-content">
              {children}
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="mobile-container">
      <AppHeader title={title} showLogout={showLogout} showBack={showBack} onBack={onBack} />
      <div className="page-content p-5">
        {children}
      </div>
      <BottomNav />
    </div>
  );
};
